import React, { useState, useEffect, useRef } from 'react';
import DataFreshness from './DataFreshness';
import {analyticsFetch as fetch,useAnalyticsPolling} from './analyticsPolling';
import CampaignCard from './CampaignCard';
import SearchableSelect from './SearchableSelect';

const PAGE_SIZE = 20;

const CampaignList = ({ initialCampaigns, initialStats, currentUser, freshness:initialFreshness }) => {
  const [freshness,setFreshness]=useState(initialFreshness),[refreshError,setRefreshError]=useState('');
  const displayedIds=useRef([]);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [campaigns, setCampaigns] = useState(initialCampaigns || []);
  const [stats, setStats] = useState(
    initialStats || {
      total: 0,
      delivered: 0,
      pending: 0,
      failed: 0,
    }
  );

  const [filters, setFilters] = useState({
    name: '',
    status: 'all',
    geo: 'all',
    sender: 'all',
    type: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const resetFilters = () =>
    setFilters({
      name: '',
      status: 'all',
      geo: 'all',
      sender: 'all',
      type: 'all',
      dateFrom: '',
      dateTo: '',
    });

  const hasActiveFilters =
    filters.name !== '' ||
    filters.status !== 'all' ||
    filters.geo !== 'all' ||
    filters.sender !== 'all' ||
    filters.type !== 'all' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '';

  const senderNames = Array.from(
    new Set(campaigns.map((c) => c.sender_name).filter(Boolean))
  ).sort();
  const geoNames = Array.from(
    new Set(campaigns.flatMap((c) => c.geos || []).filter(Boolean))
  ).sort();

  const STATUS_OPTIONS = [
    'pending',
    'running',
    'paused',
    'completed',
    'scheduled',
    'canceled',
  ];

  const matchesFilters = (campaign) => {
    if (
      filters.name &&
      !(campaign.name || '')
        .toLowerCase()
        .includes(filters.name.toLowerCase())
    ) {
      return false;
    }
    if (filters.status !== 'all' && campaign.status !== filters.status) {
      return false;
    }
    if (filters.geo !== 'all' && !(campaign.geos || []).includes(filters.geo)) {
      return false;
    }
    if (filters.sender !== 'all' && campaign.sender_name !== filters.sender) {
      return false;
    }
    if (filters.type !== 'all') {
      const isManual = !!campaign.manual_ids;
      if (filters.type === 'manual' && !isManual) return false;
      if (filters.type === 'filter' && isManual) return false;
      if (filters.type === 'sms' && campaign.channel === 'email') return false;
      if (filters.type === 'email' && campaign.channel !== 'email') return false;
    }
    if (filters.dateFrom || filters.dateTo) {
      if (!campaign.created_at) return false;
      const created = new Date(campaign.created_at);
      if (filters.dateFrom && created < new Date(filters.dateFrom)) return false;
      if (filters.dateTo && created > new Date(`${filters.dateTo}T23:59:59`))
        return false;
    }
    return true;
  };

  // Tracking which campaigns are actively sending
  const activeCampaignIds = campaigns
    .filter((c) => c.status === 'running')
    .map((c) => c.id);

  // Reference to store polling timer

  const handleStatusChange = (id, status) => {
    if (!status) return;
    setCampaigns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, status } : c))
    );
  };

  // Function to fetch updated campaign data
  const fetchUpdates = async () => {
    const prevIds = campaigns.map((c) => c.id);

    try {
      const response = await fetch(`/api/v1/campaigns/stats?ids=${displayedIds.current.join(',')}`,  {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const newIds = data.campaigns.map((c) => c.id);

        setCampaigns((prevCampaigns) =>
          prevCampaigns.map((campaign) => {
            const updated = data.campaigns.find((c) => c.id === campaign.id);
            return updated ? { ...campaign, ...updated } : campaign;
          })
        );
        setStats(data.stats);setFreshness(data.freshness);setRefreshError('');
        const missingIds = prevIds.filter((id) => !newIds.includes(id));
        for (const id of missingIds) {
          const card = campaigns.find((c) => c.id === id);
          if (card && card.status !== 'running') continue;

          fetch(`/campaigns/${id}.json`)
            .then((resp) => (resp.ok ? resp.json() : null))
            .then((detailData) => {
              if (detailData) {
                setCampaigns((prev) =>
                  prev.map((c) => (c.id === id ? { ...c, ...detailData } : c))
                );
              }
            });
        }
      } else {throw new Error('Campaign metrics could not be refreshed.');}
    } catch (error) {
      setRefreshError(error.message);
      console.error('Error fetching campaign updates:', error);
    }
  };

  useAnalyticsPolling(fetchUpdates,activeCampaignIds.length>0 ? 2000 : 60000);
  const tabCounts = {
    all: campaigns.length,
    inProgress: campaigns.filter((c) => c.stats.pending > 0).length,
    completed: campaigns.filter((c) => c.stats.total === c.stats.sent).length,
    scheduled: campaigns.filter((c) => c.status === 'scheduled').length,
  };

  // Смена таба или фильтра возвращает на первую страницу.
  useEffect(() => {
    setPage(1);
  }, [activeTab, JSON.stringify(filters)]);

  const getFilteredCampaigns = () => {
    let list;
    switch (activeTab) {
      case 'inProgress':
        list = campaigns.filter((c) => c.stats.pending > 0);
        break;
      case 'completed':
        list = campaigns.filter((c) => c.stats.total === c.stats.sent);
        break;
      case 'scheduled':
        list = campaigns.filter((c) => c.status === 'scheduled');
        break;
      default:
        list = campaigns;
    }
    return list.filter(matchesFilters);
  };

  const filteredCampaigns = getFilteredCampaigns();
  const totalPages = Math.max(1, Math.ceil(filteredCampaigns.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  displayedIds.current=pageCampaigns.map((item) => item.id);

  const goToPage = (next) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='min-h-screen bg-slate-50'>
      <div className='max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        <DataFreshness freshness={freshness} error={refreshError} />
        {/* Основной заголовок и кнопка */}
        <div className='flex items-center justify-between mb-6'>
          <div>
            <h1 className='text-xl font-semibold text-slate-900'>Campaigns</h1>
            <p className='mt-1 text-sm text-slate-500'>
              Manage and track your messaging campaigns
            </p>
          </div>

          <a
            href='/campaigns/new'
            className='inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors shadow-sm'
          >
            <svg
              className='w-4 h-4 mr-1.5'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M12 6v6m0 0v6m0-6h6m-6 0H6'
              />
            </svg>
            New Campaign
          </a>
        </div>

        {/* Табы */}
        <div className='flex items-center border-b border-slate-200 mb-5'>
          <button
            onClick={() => setActiveTab('all')}
            className={`relative inline-flex items-center px-1 py-3 border-b-2 font-medium text-sm transition-colors duration-200 ease-out ${
              activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <svg
              className='w-5 h-5 mr-1.5'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
              />
            </svg>
            All
            <span className='ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600'>
              {tabCounts.all}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('inProgress')}
            className={`relative inline-flex items-center px-1 py-3 border-b-2 font-medium text-sm transition-colors duration-200 ease-out ml-8 ${
              activeTab === 'inProgress'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <svg
              className='w-5 h-5 mr-1.5'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M13 10V3L4 14h7v7l9-11h-7z'
              />
            </svg>
            In Progress
            <span className='ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600'>
              {tabCounts.inProgress}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('completed')}
            className={`relative inline-flex items-center px-1 py-3 border-b-2 font-medium text-sm transition-colors duration-200 ease-out ml-8 ${
              activeTab === 'completed'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <svg
              className='w-5 h-5 mr-1.5'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            Completed
            <span className='ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600'>
              {tabCounts.completed}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('scheduled')}
            className={`relative inline-flex items-center px-1 py-3 border-b-2 font-medium text-sm transition-colors duration-200 ease-out ml-8 ${
              activeTab === 'scheduled'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <svg
              className='w-5 h-5 mr-1.5'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            Scheduled
            <span className='ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600'>
              {tabCounts.scheduled}
            </span>
          </button>
        </div>

        <div className='bg-white rounded-lg border border-slate-200 p-3 mb-5'>
          <div className='flex flex-wrap items-end gap-3'>
            <div className='flex-1 min-w-[200px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                Search by name
              </label>
              <input
                type='text'
                value={filters.name}
                onChange={(e) => updateFilter('name', e.target.value)}
                placeholder='Campaign name…'
                className='w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
              />
            </div>

            <div className='min-w-[140px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                Status
              </label>
              <SearchableSelect
                options={STATUS_OPTIONS.map((s) => ({
                  value: s,
                  label: s.replace(/_/g, ' '),
                }))}
                value={filters.status}
                onChange={(v) => updateFilter('status', v || 'all')}
                includeAllOption
                allLabel='All statuses'
                placeholder='All statuses'
              />
            </div>

            <div className='min-w-[130px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                GEO
              </label>
              <SearchableSelect
                options={geoNames}
                value={filters.geo}
                onChange={(v) => updateFilter('geo', v || 'all')}
                includeAllOption
                allLabel='All GEO'
                placeholder='All GEO'
              />
            </div>

            <div className='min-w-[140px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                Sender
              </label>
              <SearchableSelect
                options={senderNames}
                value={filters.sender}
                onChange={(v) => updateFilter('sender', v || 'all')}
                includeAllOption
                allLabel='All senders'
                placeholder='All senders'
              />
            </div>

            <div className='min-w-[120px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                Type
              </label>
              <SearchableSelect
                options={[
                  { value: 'sms', label: 'SMS' },
                  { value: 'email', label: 'Email' },
                  { value: 'manual', label: 'Manual list' },
                  { value: 'filter', label: 'Filter (geo/deposits)' },
                ]}
                value={filters.type}
                onChange={(v) => updateFilter('type', v || 'all')}
                includeAllOption
                allLabel='All types'
                placeholder='All types'
                isSearchable={false}
              />
            </div>

            <div className='min-w-[130px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                Created from
              </label>
              <input
                type='date'
                value={filters.dateFrom}
                onChange={(e) => updateFilter('dateFrom', e.target.value)}
                className='w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
              />
            </div>

            <div className='min-w-[130px]'>
              <label className='block text-xs font-medium text-slate-500 mb-1'>
                Created to
              </label>
              <input
                type='date'
                value={filters.dateTo}
                onChange={(e) => updateFilter('dateTo', e.target.value)}
                className='w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
              />
            </div>

            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className='px-3 py-1.5 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded transition-colors'
                title='Reset filters'
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Список кампаний */}
        <div className='space-y-4'>
          {pageCampaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              currentUser={currentUser}
              onStatusChange={handleStatusChange}
              onNameChange={(id, name) => setCampaigns((prev) =>
                prev.map((item) => item.id === id ? { ...item, name } : item)
              )}
            />
          ))}

          {filteredCampaigns.length === 0 && (
            <div className='bg-white rounded-lg border border-slate-200 p-8 text-center'>
              <div className='flex justify-center'>
                <div className='p-3 rounded-full bg-slate-100 text-slate-500 inline-block'>
                  <svg
                    className='w-8 h-8'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
                    />
                  </svg>
                </div>
              </div>
              <h3 className='mt-4 text-lg font-medium text-slate-900'>
                No campaigns found
              </h3>
              <p className='mt-2 text-sm text-slate-500'>
                {activeTab === 'all'
                  ? 'Start by creating your first campaign to send messages to your customers.'
                  : `No ${activeTab} campaigns found at the moment.`}
              </p>
              <div className='mt-6'>
                <a
                  href='/campaigns/new'
                  className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  <svg
                    className='w-4 h-4 mr-1.5'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                    />
                  </svg>
                  Create New Campaign
                </a>
              </div>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            totalItems={filteredCampaigns.length}
            onChange={goToPage}
          />
        )}
      </div>
    </div>
  );
};

// Номера страниц вокруг текущей плюс первая и последняя, остальное — многоточие.
const pageNumbers = (page, totalPages) => {
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

  return sorted.reduce((acc, p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) acc.push('…');
    acc.push(p);
    return acc;
  }, []);
};

const Pagination = ({ page, totalPages, totalItems, onChange }) => {
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalItems);
  const navButton = (disabled) =>
    `px-3 py-1.5 text-sm rounded border border-slate-200 ${
      disabled ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 bg-white hover:bg-slate-50'
    }`;

  return (
    <div className='flex items-center justify-between mt-6'>
      <p className='text-sm text-slate-500'>
        {from}–{to} of {totalItems}
      </p>
      <div className='flex items-center gap-1'>
        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          className={navButton(page === 1)}
        >
          Previous
        </button>
        {pageNumbers(page, totalPages).map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className='px-2 text-sm text-slate-400'>
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`min-w-[2.25rem] px-2 py-1.5 text-sm rounded border ${
                p === page
                  ? 'border-blue-500 bg-blue-50 text-blue-600 font-medium'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          className={navButton(page === totalPages)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CampaignList;
